import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box } from '@mui/material';

// Helper function for responsive font sizes
const responsiveFontSize = (baseFontSize, minFontSize = 14) => {
  return window.innerWidth < 768 
    ? `${Math.max(baseFontSize - 2, minFontSize)}px` 
    : `${baseFontSize}px`;
};

const MarkdownRenderer = ({ content }) => {
  return (
    <Box sx={{ 
      width: '100%', 
      overflowX: 'hidden',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      hyphens: 'auto',
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, children, ...props }) => (
            <h1
              style={{
                fontSize: responsiveFontSize(28),
                fontWeight: 600,
                marginBottom: '16px',
                marginTop: '24px',
              }}
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ node, children, ...props }) => (
            <h2
              style={{
                fontSize: responsiveFontSize(24),
                fontWeight: 600,
                marginBottom: '14px',
                marginTop: '22px',
              }}
              {...props}
            >
              {children}
            </h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3
              style={{
                fontSize: responsiveFontSize(20),
                fontWeight: 500,
                marginBottom: '12px',
                marginTop: '20px',
              }}
              {...props}
            >
              {children}
            </h3>
          ),
          p: ({ node, children, ...props }) => (
            <p
              style={{
                fontSize: responsiveFontSize(16),
                lineHeight: '1.6',
                marginBottom: '16px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                width: '100%'
              }}
              {...props}
            >
              {children}
            </p>
          ),
          li: ({ node, children, ...props }) => (
            <li
              style={{
                fontSize: responsiveFontSize(16),
                lineHeight: '1.6',
                marginBottom: '8px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto'
              }}
              {...props}
            >
              {children}
            </li>
          ),
          code({ node, inline, className, children, ...props }) {
            return (
              <code
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: inline ? '3px 6px' : '16px',
                  borderRadius: '3px',
                  fontSize: inline ? '14px' : '15px',
                  fontFamily: 'Geist Mono, monospace',
                  display: inline ? 'inline' : 'block',
                  overflowX: 'auto',
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          a: ({ node, children, ...props }) => (
            <a
              style={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto'
              }}
              {...props}
            >
              {children}
            </a>
          ),
          table: ({ node, children, ...props }) => (
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                }}
                {...props}
              >
                {children}
              </table>
            </div>
          ),
          td: ({ node, children, ...props }) => (
            <td
              style={{
                border: '1px solid #ddd',
                padding: '8px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '300px',
              }}
              {...props}
            >
              {children}
            </td>
          ),
          th: ({ node, children, ...props }) => (
            <th
              style={{
                border: '1px solid #ddd',
                padding: '8px',
                backgroundColor: '#f2f2f2',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              }}
              {...props}
            >
              {children}
            </th>
          ),
          img: ({ node, ...props }) => (
            <img
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
              {...props}
            />
          ),
        }}
        breaks={true}
      >
        {String(content || '')}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer; 